const BMFF = require('./BMFF')
const H264 = require('./H264')
const fs = require('fs')
const fsp = fs ? fs.promises : null

const MP4 = module.exports = class{
  constructor(write_fcn, seek_fcn, alloc_size) {
    this.start(write_fcn, seek_fcn, alloc_size)
  }
}

MP4.alloc_size = 16384
MP4.MOOV_TIMESCALE = 1_200_000 / 1_000
MP4.TEXT_TIMESCALE = 1_000_000 / 1_000
MP4.TEXT_TRACK = 64
MP4.SEEK_SET = 0
MP4.SEEK_CUR = 1
MP4.SEEK_END = 2

const PROTO = MP4.prototype

PROTO.reallocMemory = async function(alloc_size) {
  if(alloc_size<this.size) alloc_size = this.size
  if(alloc_size===this.data.byteLength) return
  const uint = new Uint8Array(alloc_size)
  uint.set(this.data.subarray(0, this.size), 0)
  this.data = uint
} // reallocMemory

PROTO.writeMemory = async function(uint) {
  if(uint.byteLength<=0) return 0
  if(this.pos + uint.byteLength > this.data.byteLength) {
    const new_size = Math.ceil((this.pos + uint.byteLength) / MP4.alloc_size) * MP4.alloc_size
    this.reallocMemory(new_size)
  }
  this.data.set(uint, this.pos)
  this.pos += uint.byteLength
  if(this.size<this.pos) this.size = this.pos
} // writeMemory

PROTO.seekMemory = async function(offset, whence) {
  let new_pos
  if(whence === MP4.SEEK_CUR) new_pos = this.pos + offset
  else if(whence === MP4.SEEK_SET) new_pos = 0 + offset
  else if(whence === MP4.SEEK_END) new_pos = this.size + offset
  else return
  this.pos = new_pos<0 ? 0 : new_pos>this.size ? this.size : new_pos
  return this.pos
} // seekMemory

PROTO.writeFile = async function(uint) {
  if(uint.byteLength<=0) return 0
  try{
    await this.fh.write(uint, 0, uint.byteLength, this.pos)
  }catch(e) {}
  this.pos += uint.byteLength
  if(this.size<this.pos) this.size = this.pos
} // writeFile

PROTO.seekFile = async function(offset, whence) {
  let new_pos
  if(whence === MP4.SEEK_CUR) new_pos = this.pos + offset
  else if(whence === MP4.SEEK_SET) new_pos = 0 + offset
  else if(whence === MP4.SEEK_END) new_pos = this.size + offset
  else return
  this.pos = new_pos<0 ? 0 : new_pos>this.size ? this.size : new_pos
  return this.pos
} // seekFile

PROTO.start = async function(write_fcn, seek_fcn, alloc_size) {
  if(this.fh) {
    try{ await this.fh.close() }catch(e) {}
  }
  this.data = new Uint8Array(0)
  this.size = 0
  this.pos = 0
  this.tracks = {}
  this.subtitles = []
  delete this.fh
  if(typeof (write_fcn)==='number') {
    alloc_size = write_fcn
    write_fcn = null
    seek_fcn = null
  }
  if(typeof (seek_fcn)==='number') {
    alloc_size = seek_fcn
    seek_fcn = null
  }
  if(typeof (write_fcn)==='string') {
    try{
      this.fh = await fsp.open(write_fcn, 'w')
    }catch(e) {
      delete this.fh
    }
    this.write = PROTO.writeFile
    this.seek = PROTO.seekFile
  }else if(typeof (write_fcn)==='function') {
    this.write = write_fcn
    this.seek = typeof (seek_fcn)==='function' ? seek_fcn : null
  }else{
    this.data = new Uint8Array(0)
    this.write = PROTO.writeMemory
    this.seek = PROTO.seekMemory
  }
} // start

PROTO.add = async function(usec, nal, channel) {
  let added = 0
  if(this.size===0) {
    // write MP4 initial atoms
    const ftyp = BMFF.composeBox(BMFF.getTemplate('ftyp'))
    await this.write(ftyp)
    const mdat = new Uint8Array([0, 0, 0, 0, 0x6d, 0x64, 0x61, 0x74]) // size+"mdat"
    await this.write(mdat)
    this.mdat_size_offset = ftyp.byteLength
    this.mdat_offset = ftyp.byteLength + mdat.byteLength
  }

  // find or create track structure
  channel = channel>0 ? channel : 0
  let track = this.tracks[channel]
  if(!track) {
    track = this.tracks[channel] = {
      channel,
      first_time: 0,
      last_time: 0,
      stts: [],
      stco: [],
      stsz: [],
    }
  }

  if(nal.size>4 && nal[0]===0 && nal[1]===0) {
    if(nal[2]===1) nal = nal.subarray(3)
    else if(nal[2]===0 && nal[3]===1) nal = nal.subarray(4)
  }

  const nal_type = nal[0] & 0b11111
  if(nal_type===H264.NAL.SPS) {
    track.sps = nal
    if(!track.first_sps) track.first_sps = nal
  }else if(track.sps && nal_type===H264.NAL.PPS) {
    track.pps = nal
    if(!track.first_pps) track.first_pps = nal
  }else if(track.sps && track.pps && (nal_type===H264.NAL.I || (track.keyframe && nal_type===H264.NAL.P))) {
    track.keyframe = true
    added = 1
    if(track.last_frame) {
      await this.writeTrackItem(track, track.last_frame[0], track.last_frame[1], usec)
      track.last_duration = usec - track.last_frame[1]
    }
    track.last_frame = [nal, usec]
  }
  nal = null
  return added
} // add

PROTO.addText = async function(msec, text) {
  text = String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  this.subtitles.push({ start_msec: msec, text })
} // addText

PROTO.writeText = async function(track, text, start_msec, end_msec) {
  start_msec -= this.first_time
  end_msec -= this.first_time
  if(track.last_time<end_msec) track.last_time = end_msec
  const duration = end_msec - start_msec

  const length = text.length
  const frame = new Uint8Array(2+length)
  const view = new DataView(frame.buffer)
  view.setUint16(0, length)
  for(let i=0; i<length; i++) view.setUint8(2+i, text.charCodeAt(i))

  // generate data to be pushed to MDAT
  const offset = this.mdat_offset
  this.mdat_offset += frame.byteLength
  await this.write(frame)

  // append track info
  track.stts.push(duration * MP4.TEXT_TIMESCALE)
  track.stco.push(offset)
  track.stsz.push(frame.byteLength)
} // writeText

PROTO.createTextTrack = async function() {
  // create sorted array of subtitles and add them end_msec
  let subtitles = this.subtitles.sort((a, b)=>a.start_msec-b.start_msec) // sort by time
  subtitles = subtitles.filter((sub, i)=>i===0 || subtitles[i-1].text!==sub.text) // merge subtitles with same text
  for(let i=0; i<subtitles.length-1; i++) subtitles[i].end_msec = subtitles[i+1].start_msec
  if(subtitles.length>0) subtitles[subtitles.length-1].end_msec = this.last_time

  // adjust subtitles so they are only within video times
  while(subtitles.length>0 && subtitles[0].end_msec<this.first_time) subtitles.shift()
  if(subtitles.length>0 && subtitles[0].start_msec<this.first_time) subtitles[0].start_msec=this.first_time
  while(subtitles.length>0 && subtitles[subtitles.length-1].start_msec>this.last_time) subtitles.pop()
  if(subtitles.length>0 && subtitles[subtitles.length-1].end_msec>this.last_time) subtitles[subtitles.length-1].end_msec=this.last_time

  if(subtitles.length===0) return

  const text_track = {
    channel: MP4.TEXT_TRACK,
    first_time: 0,
    last_time: 0,
    stts: [],
    stco: [],
    stsz: [],
  }
  for(const sub of subtitles) {
    await this.writeText(text_track, sub.text, sub.start_msec, sub.end_msec)
  }
  return text_track
} // createTextTrack

PROTO.end = async function() {
  // append last frame for each track
  for(const channel in this.tracks) {
    const track = this.tracks[channel]
    if(track.last_frame) { await this.writeTrackItem(track, track.last_frame[0], track.last_frame[1], track.last_frame[1]+(track.last_duration||0)) }
  }

  const moov = BMFF.getTemplate('moov')

  // add each video track to MOOV
  for(const channel of Object.keys(this.tracks).sort()) {
    const track = this.tracks[channel]
    moov._b.push(await this.constructTrak(track, 'video'))
  }

  // add text track
  const text_track = await this.createTextTrack()
  if(text_track) moov._b.push(await this.constructTrak(text_track, 'text'))

  // output constructed MOOV to output file
  BMFF.setValue(moov, 'mvhd.duration', this.last_time) // duration of longest track
  await this.write(BMFF.composeBox(moov))

  // update MDAT in the file
  const mdat_size = new Uint8Array(4)
  const mdat_size_view = new DataView(mdat_size.buffer)
  mdat_size_view.setUint32(0, this.mdat_offset-this.mdat_size_offset)
  await this.seek(this.mdat_size_offset, MP4.SEEK_SET)
  await this.write(new Uint8Array(mdat_size.buffer))

  if(this.data.byteLength>0) {
    await this.reallocMemory(0)
    const data = this.data
    await this.start()
    return data
  }else if(this.fh) {
    try{
      await this.fh.close()
    }catch(e) {}
    delete this.fh
  }
} // end

PROTO.writeTrackItem = async function(track, nal, start_msec, end_msec) {
  if(isNaN(this.first_time)) this.first_time = start_msec
  start_msec -= this.first_time
  end_msec -= this.first_time
  if(track.last_time<end_msec) track.last_time = end_msec
  const duration = end_msec - start_msec

  let frame
  const nal_type = nal[0] & 0b11111
  if(nal_type===H264.NAL.I) {
    // we always publish SPS+PPS before the keyframe
    const sps = track.sps
    const pps = track.pps
    frame = new Uint8Array(4+sps.byteLength+4+pps.byteLength+4+nal.byteLength)
    const view = new DataView(frame.buffer)
    view.setUint32(0, sps.byteLength)
    frame.set(sps, 4)
    view.setUint32(4+sps.byteLength, pps.byteLength)
    frame.set(pps, 4+sps.byteLength+4)
    view.setUint32(4+sps.byteLength+4+pps.byteLength, nal.byteLength)
    frame.set(nal, 4+sps.byteLength+4+pps.byteLength+4)
  }else{
    frame = new Uint8Array(4+nal.byteLength)
    const view = new DataView(frame.buffer)
    view.setUint32(0, nal.byteLength)
    frame.set(nal, 4)
  }

  // generate data to be pushed to MDAT
  const offset = this.mdat_offset
  this.mdat_offset += frame.byteLength
  await this.write(frame)

  if(!this.last_time || this.last_time<end_msec) this.last_time = end_msec

  // append track info
  track.stts.push(duration * MP4.MOOV_TIMESCALE)
  track.stco.push(offset)
  track.stsz.push(frame.byteLength)
} // writeTrackItem

PROTO.constructTrak = async function(track, track_type) {
  const trak = BMFF.getTemplate('trak')

  if(track_type==='video') {
    // setup video track
    const sps = H264.parseSPS(track.first_sps)
    const vmhd = BMFF.getTemplate('vmhd')
    const avc1 = BMFF.getTemplate('avc1')
    avc1.width = sps.width
    avc1.height = sps.height
    const avcC = BMFF.findBox(avc1, 'avcC')
    avcC.AVCProfileIndication = track.first_sps[1]
    avcC.profileCompatibility = track.first_sps[2]
    avcC.AVCLevelIndication = track.first_sps[3]
    avcC.sequenceParameterSets[0] = track.first_sps
    avcC.pictureParameterSets[0] = track.first_pps
    BMFF.setValue(trak, 'tkhd.trackId', track.channel)
    BMFF.setValue(trak, 'tkhd.duration', track.last_time)
    BMFF.setValue(trak, 'tkhd.alternateGroup', track.channel)
    BMFF.setValue(trak, 'tkhd.trackWidth', sps.width)
    BMFF.setValue(trak, 'tkhd.trackHeight', sps.height)
    BMFF.setValue(trak, 'tkhd.nextTrackId', 88)
    BMFF.setValue(trak, 'mdia.mdhd.timeScale', MP4.MOOV_TIMESCALE * 1000)
    BMFF.setValue(trak, 'mdia.mdhd.duration', track.last_time * MP4.MOOV_TIMESCALE)
    BMFF.setValue(trak, 'mdia.hdlr.componentSubtype', 'vide')
    BMFF.setValue(trak, 'mdia.hdlr.componentName', 'VideoHandler\x00')
    BMFF.findBox(trak, 'mdia.minf')._b.unshift(vmhd)
    BMFF.findBox(trak, 'mdia.minf.stbl.stsd')._b.unshift(avc1)
  }else if(track_type==='text') {
    const nmhd = BMFF.getTemplate('nmhd')
    const tx3g = BMFF.getTemplate('tx3g')
    BMFF.setValue(trak, 'tkhd.trackId', MP4.TEXT_TRACK)
    BMFF.setValue(trak, 'tkhd.duration', track.last_time)
    BMFF.setValue(trak, 'tkhd.alternateGroup', MP4.TEXT_TRACK)
    BMFF.setValue(trak, 'tkhd.trackWidth', 0)
    BMFF.setValue(trak, 'tkhd.trackHeight', 0)
    BMFF.setValue(trak, 'tkhd.nextTrackId', 88)
    BMFF.setValue(trak, 'mdia.mdhd.timeScale', MP4.TEXT_TIMESCALE * 1000)
    BMFF.setValue(trak, 'mdia.mdhd.duration', track.last_time * MP4.TEXT_TIMESCALE)
    BMFF.setValue(trak, 'mdia.hdlr.componentSubtype', 'sbtl')
    BMFF.setValue(trak, 'mdia.hdlr.componentName', 'SubtitleHandler\x00')
    BMFF.findBox(trak, 'mdia.minf')._b.unshift(nmhd)
    BMFF.findBox(trak, 'mdia.minf.stbl.stsd')._b.unshift(tx3g)
  }else{
    return
  }

  // stts - time durations of samples
  const stts = BMFF.findBox(trak, 'mdia.minf.stbl.stts')
  stts.count = track.stts.length
  stts.entries = new Uint8Array(stts.count*8)
  const stts_view = new DataView(stts.entries.buffer)
  for(let i=0; i<stts.count; i++) {
    stts_view.setUint32(i*8, 1)
    stts_view.setUint32(i*8+4, track.stts[i])
  }

  // we put 1 sample in 1 chunk, so all can share the same stsc
  const stsc = BMFF.findBox(trak, 'mdia.minf.stbl.stsc')
  stsc._d = '0000000000000001000000010000000100000001'

  // stco - mdat offsets of samples
  const stco = BMFF.findBox(trak, 'mdia.minf.stbl.stco')
  stco.count = track.stco.length
  stco.entries = new Uint8Array(stco.count*4)
  const stco_view = new DataView(stco.entries.buffer)
  for(let i=0; i<stco.count; i++) stco_view.setUint32(i*4, track.stco[i])

  // stsz - sizes of samples
  const stsz = BMFF.findBox(trak, 'mdia.minf.stbl.stsz')
  stsz.count = track.stsz.length
  stsz.entries = new Uint8Array(stsz.count*4)
  const stsz_view = new DataView(stsz.entries.buffer)
  for(let i=0; i<stsz.count; i++) stsz_view.setUint32(i*4, track.stsz[i])

  return trak
} // constructTrak
